import React, { ReactNode, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import AppContent from "../../components/AppContent";
import Downloading from "../../components/import-export/Downloading";
import Initial from "../../components/import-export/Initial";
import Saved from "../../components/import-export/Saved";
import Saving from "../../components/import-export/Saving";
import Uploading from "../../components/import-export/Uploading";
import ValidationFailure from "../../components/import-export/ValidationFailure";
import ValidationSuccess from "../../components/import-export/ValidationSuccess";
import ManualLink from "../../components/ManualLink";
import State from "../../State";
import intl from "../../utils/intl";
import * as actions from "./actions";
import PricingTable from "./components/PricingTable";
import messages from "./messages";
import PricingPageState from "./state";

const PricingWrapper = styled.div`
  padding-top: 20px;
`;

const PricingLayout = styled(AppContent)`
  padding: 30px;
`;

const ManualLinkWrapper = styled.div`
  float: right;
`;

function getContent(
  state: PricingPageState,
  uploadPricing: (file: File) => void,
  completePricingUpload: () => void,
  savePricing: (pricingImportId: string) => void,
  downloadPricing: () => void
): ReactNode {
  switch (state.importExportPricingState.type) {
    case "initial": {
      const { downloading, loading, pricingItems } =
        state.importExportPricingState;

      return (
        <>
          {downloading ? (
            <Downloading
              messages={{ chooseOperation: messages.chooseOperation }}
              downloading={true}
            />
          ) : (
            <Initial
              upload={uploadPricing}
              download={downloadPricing}
              chooseOperationMessage={intl.formatMessage(
                messages.chooseOperation
              )}
            />
          )}
          <PricingTable loading={loading} pricingItems={pricingItems} />
        </>
      );
    }
    case "uploading":
      return (
        <Uploading
          messages={{
            uploading: messages.uploadingPricing,
          }}
        />
      );
    case "upload-succeeded":
      return (
        <ValidationSuccess
          messages={{
            toBeSaved: messages.pricingToBeSaved,
            returnTo: messages.returnToPricing,
            save: messages.save,
          }}
          validatedData={
            <PricingTable
              loading={false}
              pricingItems={state.importExportPricingState.pricingItems}
            />
          }
          importId={state.importExportPricingState.pricingImportId}
          return={completePricingUpload}
          save={savePricing}
        />
      );
    case "upload-failed":
      return (
        <ValidationFailure
          messages={{
            uploadFailed: messages.pricingUploadFailed,
            errors: messages.errors,
            returnTo: messages.returnToPricing,
          }}
          errors={state.importExportPricingState.errors}
          complete={completePricingUpload}
        />
      );
    case "saving":
      return (
        <Saving
          messages={{
            saving: messages.savingPricing,
          }}
        />
      );
    case "saved":
      return (
        <Saved
          messages={{
            saved: messages.pricingSaved,
            return: messages.returnToPricing,
          }}
          completeUpload={completePricingUpload}
          totalCount={state.importExportPricingState.totalCount}
        />
      );
  }
}

const PricingPage: React.FC = () => {
  const dispatch = useDispatch();

  const uploadPricing = (file: File): void => {
    dispatch(actions.uploadPricing(file as File));
  };

  const completePricingUpload = (): void => {
    dispatch(actions.completePricingUpload());
  };

  const savePricing = (pricingImportId: string): void => {
    dispatch(actions.savePricing(pricingImportId));
  };

  const downloadPricing = (): void => {
    dispatch(actions.downloadPricing());
  };

  const state = useSelector<State, PricingPageState>((state) => state.pricing);

  useEffect(() => {
    if (state.importExportPricingState.type === "initial") {
      dispatch(actions.loadPricing());
    }
  }, [state.importExportPricingState.type, dispatch]);

  return (
    <>
      <PricingWrapper>
        <PricingLayout>
          <ManualLinkWrapper>
            <ManualLink fileName={"pricing_manual.docx"} />
          </ManualLinkWrapper>
          {getContent(
            state,
            uploadPricing,
            completePricingUpload,
            savePricing,
            downloadPricing
          )}
        </PricingLayout>
      </PricingWrapper>
    </>
  );
};

export default PricingPage;
